import { useState, useEffect, useRef } from 'react';
import { User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { setPreviewSession } from '../services/previewMode';
import {
    onAuthStateChange,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    handleGoogleRedirectResult,
    sendMagicLink,
    signOut,
} from '../services/authService';
import { UserProfile, UserRole } from '../types';

export interface AuthState {
    user: User | null;
    userProfile: UserProfile | null;
    loading: boolean;
    error: string | null;
    /** Role from JWT Claims — authoritative source of truth for security */
    claimsRole: UserRole | null;
    /** Organization ID from JWT Claims — null for legacy users */
    claimsOrgId: string | null;
    /** first onAuthStateChanged callback has fired (boot machine input) */
    authResolved: boolean;
    /** a token read (cached or fresh) completed for the current user */
    claimsKnown: boolean;
    /** first users/{uid} snapshot arrived or timed out */
    profileSettled: boolean;
    /** a pending provision exists for this email but the email is unverified —
        the user must click the verification link before roles can attach */
    awaitingEmailVerification: boolean;
}

export interface AuthActions {
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string, displayName: string, role: UserRole) => Promise<void>;
    signInGoogle: (role: UserRole) => Promise<void>;
    sendEmailLink: (email: string, role: UserRole) => Promise<void>;
    logout: () => Promise<void>;
    clearError: () => void;
    refreshClaims: () => Promise<void>;
}

export const useAuth = (): AuthState & AuthActions => {
    const [user, setUser] = useState<User | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [claimsRole, setClaimsRole] = useState<UserRole | null>(null);
    const [claimsOrgId, setClaimsOrgId] = useState<string | null>(null);
    const [authResolved, setAuthResolved] = useState(false);
    const [claimsKnown, setClaimsKnown] = useState(false);
    const [profileSettled, setProfileSettled] = useState(false);
    const [awaitingEmailVerification, setAwaitingEmailVerification] = useState(false);

    // Store the unsubscribe function for the userProfile listener
    const userProfileUnsubscribeRef = useRef<(() => void) | null>(null);
    // Track whether the first onAuthStateChanged call has settled —
    // prevents re-gating loading on Firebase session-restore (second call).
    const hasSettledRef = useRef(false);
    // One pending-provision lookup per session — without this, a user who
    // genuinely has no invite would call the function on every auth callback.
    const pendingClaimTriedRef = useRef(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChange(async (firebaseUser) => {
            setUser(firebaseUser);
            setAuthResolved(true);
            if (firebaseUser && !hasSettledRef.current) {
                // Fresh boot of a new user — reset granular readiness flags
                setClaimsKnown(false);
                setProfileSettled(false);
            }

            // Clean up any existing userProfile listener
            if (userProfileUnsubscribeRef.current) {
                userProfileUnsubscribeRef.current();
                userProfileUnsubscribeRef.current = null;
            }

            if (firebaseUser) {
                // Only re-gate loading on the very first auth callback.
                // Subsequent calls (session restore) keep the current UI visible
                // while claims + profile refresh in the background.
                if (!hasSettledRef.current) {
                    setLoading(true);
                }

                // Force-refresh JWT claims from server — cached tokens can
                // carry stale roles (e.g. participant→admin upgrade).
                // Use cached token as fallback on session-restore to avoid
                // blocking the UI on a network call.
                try {
                    let tokenResult = await firebaseUser.getIdTokenResult(true);

                    // Invite-first onboarding: a superadmin can assign a role to
                    // an email before that person has an account. With no role
                    // claim, ask the server whether an invite is waiting; if it
                    // applies one, re-read the token so this session picks it up
                    // instead of dead-ending on the unprovisioned screen.
                    if (!tokenResult.claims.role && !pendingClaimTriedRef.current) {
                        pendingClaimTriedRef.current = true;
                        try {
                            const { httpsCallable } = await import('firebase/functions');
                            const { functions } = await import('../services/firebaseConfig');
                            const claim = httpsCallable(functions, 'claimPendingProvision');
                            const res = await claim({});
                            const claimData = res.data as { claimed?: boolean; reason?: string };
                            if (claimData?.claimed) {
                                tokenResult = await firebaseUser.getIdTokenResult(true);
                            } else if (claimData?.reason === 'unverified_email') {
                                // A provision may be waiting but cannot attach
                                // until the email is verified — surface it so
                                // UnprovisionedView explains instead of
                                // dead-ending on "ask your administrator".
                                setAwaitingEmailVerification(true);
                            }
                        } catch (err) {
                            console.warn('[useAuth] pending provision check failed', err);
                        }
                    }

                    const claims = tokenResult.claims;
                    if (claims.role) setAwaitingEmailVerification(false);
                    setClaimsRole((claims.role as UserRole) ?? null);
                    setClaimsOrgId((claims.orgId as string) ?? null);
                    // Admin-tier users never write participant data — anything
                    // they submit from the participant view is a preview.
                    setPreviewSession(
                        ['superadmin', 'orgadmin', 'researcher'].includes(claims.role as string),
                    );
                } catch {
                    // If token refresh fails (e.g. offline), try cached token
                    try {
                        const cached = await firebaseUser.getIdTokenResult(false);
                        setClaimsRole((cached.claims.role as UserRole) ?? null);
                        setClaimsOrgId((cached.claims.orgId as string) ?? null);
                    } catch {
                        setClaimsRole(null);
                        setClaimsOrgId(null);
                    }
                }
                setClaimsKnown(true);

                // Set up real-time listener for user profile.
                // setLoading(false) is deferred until the first snapshot arrives —
                // prevents guards from making decisions while userProfile is still null.
                const userDocRef = doc(db, 'users', firebaseUser.uid);
                let isFirstSnapshot = true;

                // Safety timeout — if onSnapshot never fires (e.g. SW killed
                // the Firestore WebChannel), unblock the UI after 5s.
                const snapshotTimeout = setTimeout(() => {
                    if (isFirstSnapshot) {
                        console.warn('useAuth: onSnapshot timed out, unblocking UI');
                        isFirstSnapshot = false;
                        hasSettledRef.current = true;
                        setLoading(false);
                        setProfileSettled(true);
                    }
                }, 5000);

                userProfileUnsubscribeRef.current = onSnapshot(
                    userDocRef,
                    (snapshot) => {
                        if (snapshot.exists()) {
                            setUserProfile(snapshot.data() as UserProfile);
                        } else {
                            setUserProfile(null);
                        }
                        if (isFirstSnapshot) {
                            isFirstSnapshot = false;
                            clearTimeout(snapshotTimeout);
                            hasSettledRef.current = true;
                            setLoading(false);
                            setProfileSettled(true);
                        }
                    },
                    (error) => {
                        console.error('Error listening to user profile:', error);
                        setUserProfile(null);
                        if (isFirstSnapshot) {
                            isFirstSnapshot = false;
                            clearTimeout(snapshotTimeout);
                            hasSettledRef.current = true;
                            setLoading(false);
                            setProfileSettled(true);
                        }
                    }
                );
            } else {
                setUserProfile(null);
                setClaimsRole(null);
                setClaimsOrgId(null);
                setAwaitingEmailVerification(false);
                // NOT settled: a signed-out state must treat the NEXT login as
                // a fresh boot (re-gate loading/claimsKnown/profileSettled).
                // Leaving this true made post-logout logins skip re-gating, so
                // gates saw user + null claims + loading=false during the JWT
                // fetch — the "Account not activated" flash before admin view.
                hasSettledRef.current = false;
                // Per-page-life latch would otherwise deny a different uid its
                // one claimPendingProvision attempt after a sign-out.
                pendingClaimTriedRef.current = false;
                setLoading(false);
                setClaimsKnown(true);
                setProfileSettled(true);
            }
        });

        // Handle redirect result from mobile Google sign-in
        handleGoogleRedirectResult().catch(() => {});

        // Cleanup function to unsubscribe from both listeners
        return () => {
            unsubscribe();
            if (userProfileUnsubscribeRef.current) {
                userProfileUnsubscribeRef.current();
                userProfileUnsubscribeRef.current = null;
            }
        };
    }, []);

    const signIn = async (email: string, password: string): Promise<void> => {
        try {
            setError(null);
            setLoading(true);
            await signInWithEmail(email, password);
            // Don't setLoading(false) here — onAuthStateChange will set it
            // after claims refresh + first profile snapshot arrive.
        } catch (err: any) {
            setLoading(false);
            setError(err.message);
            throw err;
        }
    };

    const signUp = async (
        email: string,
        password: string,
        displayName: string,
        role: UserRole
    ): Promise<void> => {
        try {
            setError(null);
            setLoading(true);
            await signUpWithEmail(email, password, displayName, role);
        } catch (err: any) {
            setLoading(false);
            setError(err.message);
            throw err;
        }
    };

    const signInGoogle = async (role: UserRole): Promise<void> => {
        try {
            setError(null);
            setLoading(true);
            await signInWithGoogle(role);
        } catch (err: any) {
            setLoading(false);
            setError(err.message);
            throw err;
        }
    };

    const sendEmailLink = async (email: string, role: UserRole): Promise<void> => {
        try {
            setError(null);
            setLoading(true);
            await sendMagicLink(email, role);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const logout = async (): Promise<void> => {
        try {
            setError(null);
            await signOut();
        } catch (err: any) {
            setError(err.message);
            throw err;
        }
    };

    const clearError = (): void => {
        setError(null);
    };

    const refreshClaims = async (): Promise<void> => {
        if (!user) return;
        try {
            const token = await user.getIdTokenResult(true);
            setClaimsRole((token.claims.role as UserRole) ?? null);
            setClaimsOrgId((token.claims.orgId as string) ?? null);
        } catch (error) {
            console.error('Error refreshing claims:', error);
        }
    };

    return {
        user,
        userProfile,
        loading,
        error,
        claimsRole,
        claimsOrgId,
        authResolved,
        claimsKnown,
        profileSettled,
        awaitingEmailVerification,
        signIn,
        signUp,
        signInGoogle,
        sendEmailLink,
        logout,
        clearError,
        refreshClaims,
    };
};
